import {
    useDisclosure,
    HStack,
    IconButton,
    Text,
    Box,
    Flex,
    AlertTitle,
} from '@chakra-ui/react';
import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    useMemo,
    useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LiaHashtagSolid } from 'react-icons/lia';

import Tooltip from 'components/v4/Tooltip';
import { Alert, AlertIcon } from '@/components/v4';
import ServiceWarningBanner from 'components/maintenance/ServiceWarningBanner';
import { TroubleshootModal } from '@/pages/accounts/resourceAccounts/table';
import useGetAppAuthByCompanyId from '@/queries/appAuth/useGetAppAuthbyCompanyId';
import useGetCompanyServices from '@/queries/appAuth/useGetCompanyServices';

import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import PlatformFilterStack from 'components/tables/AGGrid/PlatformFilterStack';
import {
    PLATFORM_STACK_MIN_WIDTH,
    getPlatformStackFullWidth,
} from 'components/tables/AGGrid/platformFilterStackSizing';
import { getButtonGroupFullWidth } from 'components/presentational/collapsibleButtonGroupSizing';
import UserStatusCellRenderer from '../cellRenderer/UserStatusCellRenderer';
import TelephoneNumberCellRenderer from '../cellRenderer/TelephoneNumberCellRenderer';
import PlatformCellRenderer from 'components/tables/AGGrid/CustomCells/PlatformCellRenderer';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import DisplayNameRenderer from '../cellRenderer/DisplayNameRenderer';
import UserEditDrawer from './UserEditDrawer';
import UsersButtons from './UsersButtons';
import { getUsersButtonVisibility } from './usersButtonVisibility';
import Platforms, { PlatformEnums } from '../../../../../utils/platforms';
import useGetTCAPUsers from '../../queries/useGetTCAPUsers';
import useGetTCAPUserByID from '../../queries/useGetTCAPUserByID';
import SummaryPill from 'components/v4/Pills/SummaryPill';
import { BsPerson, BsPersonCheck, BsThreeDotsVertical } from 'react-icons/bs';
import useGetCompanyPlatformUsers from '../../queries/useGetCompanyPlatformUsers';
import CountryFlag from 'components/icons/CountryFlag';
import { tableFlagStyle } from 'components/icons/countryFlagStyles';
import Statuses from 'pages/accounts/users/utils/statuses';
import { getTelephoneNumber } from 'utils/utils';
import { TelephoneNumberFormatter } from 'pages/accounts/users/new/cellRenderer/TelephoneNumberFormatter';
import { ServiceEnums } from 'utils/enums/Services';

// Matches the naming convention used by the Users CSV template download
const makeDateStr = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yy}${mm}${dd}_${hh}${min}${ss}`;
};

const slugify = (str) =>
    (str || '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');

const PLATFORM_FILTER_KEY = 'usersSelectedPlatform';

// Stored with the company it belongs to — platforms differ per company, so a
// selection from a previous company must not be restored.
const readPersistedPlatform = (companyId) => {
    const raw = sessionStorage.getItem(PLATFORM_FILTER_KEY);
    if (!raw) return undefined;
    try {
        const stored = JSON.parse(raw);
        return stored?.companyId === companyId ? stored.platform : undefined;
    } catch {
        return undefined;
    }
};

const persistPlatform = (companyId, platform) => {
    if (platform) {
        sessionStorage.setItem(
            PLATFORM_FILTER_KEY,
            JSON.stringify({ companyId, platform }),
        );
    } else {
        sessionStorage.removeItem(PLATFORM_FILTER_KEY);
    }
};

export default function Users() {
    const gridRef = useRef();
    const expandedUserIdRef = useRef(null);
    const expandedDataRef = useRef(null);
    const { pathname } = useLocation();
    const { isOpen, onClose } = useDisclosure();
    const { currentScope } = useSelector((state) => {
        return { ...state.login };
    });
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany, currentCompanyData, currentCompanyPlatforms } =
        useSelector((state) => state.navigationLists);

    const [hasNoUsers, setHasNoUsers] = useState(false);
    const [isEditUserDrawerOpen, setIsEditUserDrawerOpen] = useState(false);
    const setUserDrawerClose = () => setIsEditUserDrawerOpen(false);
    const [userId, setUserId] = useState();
    const [summaryData, setSummaryData] = useState([]);
    const [expandedPlatformUsers, setExpandedPlatformUsers] = useState();
    const setExpandedPlatformUsersToEmpty = () => setExpandedPlatformUsers([]);
    // Clicking the Users sidebar item while already on the page remounts this
    // component through /reload, which used to reset the platform filter and
    // silently swap the grid back to the TCAP view — a different dataset with a
    // different sort order. Persisting the selection keeps the same view.
    const [selectedPlatform, setSelectedPlatformState] = useState(() =>
        readPersistedPlatform(currentCompany),
    );
    const setSelectedPlatform = useCallback(
        (platform) => {
            setSelectedPlatformState(platform);
            persistPlatform(currentCompany, platform);
        },
        [currentCompany],
    );
    const [editedPlatform, setEditedPlatform] = useState();
    const [primaryUser, setPrimaryUser] = useState();
    const [filteredPlatforms, setFilteredPlatforms] = useState([]);

    const { data: appAuth, isFetched: appAuthFetched } =
        useGetAppAuthByCompanyId(currentCompany);
    const { data: companyServices } = useGetCompanyServices(currentCompany);

    const hasTPM = companyServices?.some(
        (service) => service.id === ServiceEnums.TEAMS_PHONE_MOBILE,
    );

    const getTpmOperatorProps = useCallback(
        (user) => {
            if (!hasTPM || user?.operatorStatus === 3) return {};
            return {
                operatorStatus: user?.operatorStatus,
                operatorMessage: user?.operatorMessage,
            };
        },
        [hasTPM],
    );

    const [PlatformColDefs, setPlatformColDefs] = useState([
        {
            field: 'platformID',
            headerName: 'Platform',
            width: 120,
            flex: 0.6,
            cellRenderer: PlatformCellRenderer,
            hide: true,
            filter: 'agSetColumnFilter',
            filterParams: {
                valueFormatter: (params) => {
                    return Platforms[params.value].name;
                },
            },
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
        },
        {
            headerName: 'Display Name',
            field: 'displayName',
            cellRenderer: DisplayNameRenderer,
            wrapText: false,
            autoHeight: false,
            cellStyle: () => {
                return {
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                };
            },
        },
        {
            field: 'status',
            cellRenderer: UserStatusCellRenderer,
            cellRendererParams: (params) => ({
                value: params.value,
                returnedMessage: params.data.returnedMessage,
            }),
            filter: 'agSetColumnFilter',
            cellStyle: { display: 'flex', alignItems: 'center' },
            valueFormatter: (params) => {
                return Statuses[params?.value]?.displayedName;
            },
            filterParams: {
                cellRenderer: (params) => {
                    const isSelectedAll = params?.value === '(Select All)';
                    if (!isSelectedAll) {
                        return (
                            <Text isTruncated>
                                {Statuses[params?.value]?.displayedName}
                            </Text>
                        );
                    } else {
                        return '(Select All)';
                    }
                },
            },
        },

        {
            headerName: 'Type',
            field: 'userType',
            valueFormatter: (params) => {
                return params?.value === 'ResourceAccount'
                    ? 'Resource Account'
                    : params?.value === 'RingGroup'
                      ? 'Ring Group'
                      : 'User';
            },
            filterParams: {
                cellRenderer: (params) => {
                    const isSelectedAll = params?.value === '(Select All)';
                    const value =
                        params.value === 'ResourceAccount'
                            ? 'Resource Account'
                            : params.value === 'RingGroup'
                              ? 'Ring Group'
                              : 'User';
                    const content = isSelectedAll ? '(Select All)' : value;
                    return content;
                },
            },
        },
        {
            headerName: 'Username',
            field: 'userPrincipalName',
        },
        {
            headerName: 'Number',
            field: 'telephoneNumber',
            cellRenderer: (params) => (
                <TelephoneNumberFormatter
                    value={params.value}
                    {...getTpmOperatorProps(params.data)}
                />
            ),
        },
        {
            headerName: 'Usage Location',
            field: 'usageLocation',
            cellRenderer: (params) => {
                const countryCode = params.value;
                return (
                    <Flex alignItems="center" justify="center" height="100%">
                        {CountryFlag({ countryCode, style: tableFlagStyle })}
                    </Flex>
                );
            },
        },
        {
            headerName: 'Managed By',
            field: 'managedBy',
            cellRendererParams: {
                suppressPadding: false,
                innerRenderer: CopyCellRenderer,
            },
            cellRenderer: (params) => {
                let value;
                if (params.value === 0) {
                    value = 'TCAP';
                } else if (params.data.platformID === PlatformEnums.GCC) {
                    value = 'Microsoft Teams';
                } else {
                    value = Platforms[params.data.platformID]?.name || 'Other';
                }
                return CopyCellRenderer({ value });
            },
            valueFormatter: (params) => {
                const value =
                    Number(params.value) === 0
                        ? 'TCAP'
                        : Platforms[params?.data?.platformID]?.name || 'Other';
                return value;
            },
            filter: 'agSetColumnFilter',
            filterParams: {
                cellRenderer: (params) => {
                    const isSelectedAll = params?.value === '(Select All)';
                    const value = params.value === 0 ? 'TCAP' : 'Other';
                    const content = isSelectedAll ? '(Select All)' : value;
                    return <>{content}</>;
                },
            },
        },
        {
            headerName: 'Platform Attribute 1',
            field: 'platformAttribute1',
            hide: true,
            cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
            headerName: 'Platform Attribute 2',
            field: 'platformAttribute2',
            hide: true,
            cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
            headerName: 'Platform Attribute 3',
            field: 'platformAttribute3',
            hide: true,
            cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
            maxWidth: 100,
            headerName: 'Action',
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            sortable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            lockPosition: 'right',
            cellRenderer: (params) => {
                return (
                    <HStack
                        spacing={0}
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        height="100%">
                        <Tooltip label="Edit Platform User">
                            <IconButton
                                icon={
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill={darkMode ? 'white' : 'black'}
                                        xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="5" r="1.5" />
                                        <circle cx="12" cy="12" r="1.5" />
                                        <circle cx="12" cy="19" r="1.5" />
                                    </svg>
                                }
                                aria-label="Edit"
                                variant="ghost"
                                onClick={() => {
                                    handleEditBtn(params.data);
                                }}
                            />
                        </Tooltip>
                    </HStack>
                );
            },
        },
    ]);

    useEffect(() => {
        const newFilteredPlatforms = currentCompanyPlatforms
            ?.filter((platform) => platform.userSyncPolicy !== 0)
            .filter(
                (platform) =>
                    platform.id === PlatformEnums.EIGHTBYEIGHT ||
                    appAuth?.some((auth) => auth.platformID === platform.id),
            )
            .map((platform) => ({
                id: platform.id,
                name:
                    Platforms[platform.id]?.name ||
                    platform.name ||
                    'Unknown Platform',
            }))
            .filter(
                (platform, index, self) =>
                    index === self.findIndex((p) => p.id === platform.id),
            )
            .filter(
                (platform) =>
                    platform.id !== PlatformEnums.SBC &&
                    platform.id !== PlatformEnums.AZURE,
            );
        setFilteredPlatforms(newFilteredPlatforms);
    }, [currentCompanyPlatforms, appAuth]);

    // A restored selection can be stale (platform removed from the company), in
    // which case fall back to the default TCAP view rather than filtering on a
    // platform that no longer exists.
    useEffect(() => {
        if (!selectedPlatform?.id || !filteredPlatforms?.length) return;
        const stillAvailable = filteredPlatforms.some(
            (platform) => platform.id === selectedPlatform.id,
        );
        if (!stillAvailable) {
            setSelectedPlatform(undefined);
        }
    }, [filteredPlatforms, selectedPlatform, setSelectedPlatform]);

    const hasSetDefaultPlatform = useRef(false);

    // Remove the first useGetCompanyPlatformUsers hook and use the one defined later in the code
    useEffect(() => {
        // Wait for app auth before defaulting. 8x8 is the one platform exempt
        // from the app-auth gate below, so while appAuth is still unresolved the
        // filter keeps only 8x8 — a multi-platform company looks like a
        // single-platform one and gets silently switched to the 8x8 view.
        // appAuth is fetched with cacheTime 0, so this happens on every mount.
        if (!appAuthFetched) return;

        const filteredPlatforms = currentCompanyPlatforms
            ?.filter((platform) => platform.userSyncPolicy !== 0)
            .filter(
                (platform) =>
                    platform.id === PlatformEnums.EIGHTBYEIGHT ||
                    appAuth?.some((auth) => auth.platformID === platform.id),
            )
            .map((platform) => ({
                id: platform.id,
                name:
                    Platforms[platform.id]?.name ||
                    platform.name ||
                    'Unknown Platform',
            }))
            .filter(
                (platform, index, self) =>
                    index === self.findIndex((p) => p.id === platform.id),
            )
            .filter(
                (platform) =>
                    platform.id !== PlatformEnums.SBC &&
                    platform.id !== PlatformEnums.AZURE,
            );
        if (filteredPlatforms?.length === 1) {
            const platform = filteredPlatforms[0];
            if (platform?.id) {
                // Deliberately the non-persisting setter: this is a derived
                // default, not a user choice, and appAuth is uncached so this
                // effect first runs with an incomplete platform list. Writing it
                // to sessionStorage would make that transient default stick.
                setSelectedPlatformState(platform);
                setPlatformColDefs((prev) => {
                    if (prev.length > 0) {
                        // Set the first column's hide property to true
                        const updated = [...prev];
                        updated[0] = { ...updated[0], hide: true };
                        return updated;
                    }
                    return prev;
                });
                hasSetDefaultPlatform.current = true;
                // refetchPlatformUsers will be available from the later hook
            }
        }
    }, [currentCompanyPlatforms, appAuth, appAuthFetched]);

    const { refetch: platformRefetch } = useGetTCAPUserByID(
        userId,
        setPrimaryUser,
    );

    const handleEditBtn = useCallback(
        async (userData) => {
            const tcapUserId = userData.tcapUserID;

            expandedUserIdRef.current = tcapUserId;
            setUserId(tcapUserId);
            setEditedPlatform(userData);

            setTimeout(async () => {
                const platformUsersWait = await platformRefetch();
                if (platformUsersWait) {
                    const prioritisedRes = platformUsersWait.data.sort(
                        (a, b) => a.priority - b.priority,
                    );
                    setExpandedPlatformUsers(prioritisedRes);
                    setIsEditUserDrawerOpen(true);
                }
            }, 0);
        },
        [platformRefetch],
    );

    const rowDetailEditable = (params) => params?.data?.isLicensed === true;
    const baseColumns = useMemo(
        () => [
            {
                maxWidth: 70,
                width: 70,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellRenderer: 'agGroupCellRenderer',
                lockPosition: 'left',
            },
            {
                headerName: 'Display Name',
                field: 'displayName',
                cellRenderer: DisplayNameRenderer,
                // Declared here rather than left to useGetTCAPUsers' imperative
                // applyColumnState call: on a menu reload the cached data is
                // already present before the grid API exists, so that call
                // no-ops and the rows come back in raw API order.
                sort: 'asc',
                wrapText: false,
                autoHeight: false,
                cellStyle: () => {
                    return {
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    };
                },
            },
            {
                headerName: 'Username',
                field: 'primaryUser.userPrincipalName',
            },
            {
                headerName: 'Status',
                field: 'status',
                suppressHeaderMenuButton: true,
                filter: 'agSetColumnFilter',
                cellRenderer: (params) => {
                    return UserStatusCellRenderer({ value: params.value });
                },
                valueFormatter: (params) => {
                    return Statuses[params?.value]?.displayedName;
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const statusLookup = Statuses[params?.value];
                        const displayName = statusLookup?.displayedName;
                        if (!isSelectedAll) {
                            return <Text isTruncated>{displayName}</Text>;
                        } else {
                            return '(Select All)';
                        }
                    },
                },
            },
            {
                headerName: 'Type',
                field: 'primaryUser.userType',
                valueFormatter: (params) => {
                    return params?.value === 'ResourceAccount'
                        ? 'Resource Account'
                        : params?.value === 'RingGroup'
                          ? 'Ring Group'
                          : 'User';
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value =
                            params.value === 'ResourceAccount'
                                ? 'Resource Account'
                                : params.value === 'RingGroup'
                                  ? 'Ring Group'
                                  : 'User';
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
            },
            {
                headerName: 'Licensed',
                field: 'primaryUser.isLicensed',
                hide: true,
                cellRenderer: (params) => {
                    return CopyCellRenderer({
                        value: params.value ? 'Yes' : 'No',
                    });
                },
                valueFormatter: (params) => {
                    return params.value === 'false' ? 'No' : 'Yes';
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value = params.value ? 'Yes' : 'No';
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
            },
            {
                maxWidth: 100,
                headerName: 'Action',
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressHeaderMenuButton: true,
                suppressColumnsToolPanel: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                lockPosition: 'right',
                cellRenderer: (params) => {
                    return (
                        <HStack
                            spacing={0}
                            justifyContent="center"
                            alignItems="center"
                            width="100%"
                            height="100%">
                            {/* <Tooltip label="More actions"> */}
                            <IconButton
                                icon={
                                    <BsThreeDotsVertical
                                        fill={darkMode ? 'white' : 'black'}
                                    />
                                }
                                aria-label="More actions"
                                variant="ghost"
                                onClick={() => {
                                    handleEditBtn(params.data.primaryUser);
                                }}
                                _hover={{
                                    bg: darkMode
                                        ? 'whiteAlpha.200'
                                        : 'blackAlpha.100',
                                }}
                                _active={{
                                    bg: darkMode
                                        ? 'whiteAlpha.300'
                                        : 'blackAlpha.200',
                                }}
                                _focus={{ boxShadow: 'none' }}
                            />
                            {/* </Tooltip> */}
                        </HStack>
                    );
                },
            },
        ],
        [darkMode, handleEditBtn],
    );

    const defaultCellRendererColDef = useMemo(() => {
        return {
            sortable: false,
            filter: false,
            resizable: true,
            flex: 1,
            minWidth: 100,
            suppressHeaderMenuButton: true,
            cellRenderer: CopyCellRenderer,
        };
    }, []);

    const detailCellRendererParams = useMemo(
        () => ({
            detailGridOptions: {
                pagination: false,
                suppressHorizontalScroll: true,
                columnDefs: [
                    {
                        field: 'platformID',
                        headerName: 'Platform',
                        width: 120,
                        flex: 0.6,
                        cellRenderer: PlatformCellRenderer,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            valueFormatter: (params) => {
                                return Platforms[params.value].name;
                            },
                        },
                        cellStyle: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        },
                    },
                    {
                        headerName: 'Display Name',
                        field: 'displayName',
                        cellRenderer: DisplayNameRenderer,
                        wrapText: false,
                        autoHeight: false,
                        cellStyle: () => {
                            return {
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            };
                        },
                    },
                    {
                        field: 'status',
                        cellStyle: { display: 'flex', alignItems: 'center' },
                        cellRenderer: UserStatusCellRenderer,
                        cellRendererParams: (params) => ({
                            value: params.value,
                            returnedMessage: params.data.returnedMessage,
                        }),
                    },
                    {
                        headerName: 'Username',
                        field: 'userPrincipalName',
                    },
                    {
                        headerName: 'Number',
                        field: 'userProfiles[0]?.platformNumber?.telephoneNumber',
                        cellRenderer: (params) => (
                            <TelephoneNumberCellRenderer
                                data={params.data}
                                {...getTpmOperatorProps(params.data)}
                            />
                        ),
                    },
                    {
                        headerName: 'Usage Location',
                        field: 'usageLocation',
                        displayName: 'Usage Location',
                        cellRenderer: (params) => {
                            const countryCode = params.value;
                            if (!countryCode) return null;
                            return (
                                <Flex
                                    alignItems="center"
                                    justify="center"
                                    height="100%">
                                    {CountryFlag({
                                        countryCode,
                                        style: tableFlagStyle,
                                    })}
                                </Flex>
                            );
                        },
                    },
                    {
                        headerName: 'Managed By',
                        field: 'managedBy',
                        cellRendererParams: {
                            suppressPadding: false,
                            innerRenderer: CopyCellRenderer,
                        },
                        cellRenderer: (params) => {
                            const value =
                                params.value === 0
                                    ? 'TCAP'
                                    : Platforms[params.data.platformID]?.name ||
                                      'Other';
                            return CopyCellRenderer({ value });
                        },
                    },
                    {
                        maxWidth: 140,
                        headerName: 'Action',
                        filter: false,
                        suppressMovable: true,
                        lockVisible: true,
                        suppressHeaderMenuButton: true,
                        suppressHeaderFilterButton: true,
                        enableRowGroup: false,
                        resizable: false,
                        sortable: false,
                        cellStyle: () => ({
                            display: 'flex',
                            alignItems: 'center',
                        }),
                        cellRenderer: (params) => {
                            return (
                                rowDetailEditable(params) && (
                                    <HStack
                                        spacing={0}
                                        justifyContent="center"
                                        alignItems="center"
                                        width="100%"
                                        height="100%">
                                        <Tooltip label="Edit Platform User">
                                            <IconButton
                                                icon={
                                                    <BsThreeDotsVertical
                                                        fill={
                                                            darkMode
                                                                ? 'white'
                                                                : 'black'
                                                        }
                                                    />
                                                }
                                                aria-label="Edit"
                                                variant="ghost"
                                                onClick={() => {
                                                    rowDetailEditable(params) &&
                                                        handleEditBtn(
                                                            params.data,
                                                        );
                                                }}
                                                _hover={{
                                                    bg: darkMode
                                                        ? 'whiteAlpha.200'
                                                        : 'blackAlpha.100',
                                                }}
                                                _active={{
                                                    bg: darkMode
                                                        ? 'whiteAlpha.300'
                                                        : 'blackAlpha.200',
                                                }}
                                                _focus={{ boxShadow: 'none' }}
                                            />
                                        </Tooltip>
                                    </HStack>
                                )
                            );
                        },
                    },
                    // {
                    //     headerName: 'Licensed',
                    //     field: 'isLicensed',
                    //     cellRenderer: (params) => {
                    //         return CopyCellRenderer({
                    //             value: params.value ? 'Yes' : 'No',
                    //         });
                    //     },
                    // },
                ],
                headerHeight: 38,
                defaultColDef: defaultCellRendererColDef,
            },
            getDetailRowData: (params) => {
                const newUserId = params.data.id;

                if (expandedUserIdRef.current === newUserId) {
                    if (expandedDataRef.current) {
                        params.successCallback(expandedDataRef.current);
                        return;
                    }
                }

                expandedUserIdRef.current = newUserId;
                expandedDataRef.current = null;

                if (userId !== newUserId) {
                    setUserId(newUserId);
                }

                (async () => {
                    try {
                        if (userId !== newUserId) {
                            await new Promise((resolve) =>
                                setTimeout(resolve, 0),
                            );
                        }

                        const res = await platformRefetch();
                        if (res?.data) {
                            const prioritisedRes = res.data
                                .sort((a, b) => a.priority - b.priority)
                                .map((r) => ({
                                    ...r,
                                    displayName:
                                        r.displayName ||
                                        [r.givenName, r.surname]
                                            .filter(Boolean)
                                            .join(' ') ||
                                        r.userPrincipalName ||
                                        '',
                                }));
                            expandedDataRef.current = prioritisedRes; // Cache the data
                            setExpandedPlatformUsers(prioritisedRes);
                            params.successCallback(prioritisedRes);
                        } else {
                            params.successCallback([]);
                        }
                    } catch (error) {
                        console.error('Error fetching platform users:', error);
                        params.successCallback([]);
                    }
                })();
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            darkMode,
            defaultCellRendererColDef,
            handleEditBtn,
            platformRefetch,
            getTpmOperatorProps,
        ],
    );

    const [tableData, setTableData] = useState({
        columns: baseColumns,
        data: [],
        detailCellRendererParams: detailCellRendererParams,
    });

    const [platformsTableData, setPlatformsTableData] = useState({
        columns: PlatformColDefs,
        data: [],
    });

    const tableDataWithColumns = useMemo(
        () => ({
            columns: baseColumns,
            data: tableData.data,
            detailCellRendererParams: detailCellRendererParams,
        }),
        [baseColumns, tableData.data, detailCellRendererParams],
    );

    const platformsTableDataWithColumns = useMemo(
        () => ({
            columns: PlatformColDefs,
            data: platformsTableData.data,
        }),
        [PlatformColDefs, platformsTableData.data],
    );

    const { isLoading, refetch, isRefetching, isFetching } = useGetTCAPUsers(
        filteredPlatforms?.length > 1 ? currentCompany : null,
        setHasNoUsers,
        setTableData,
        gridRef,
        setSummaryData,
    );
    const {
        isRefetching: isPlatformUsersRefetching,
        refetch: refetchPlatformUsers,
    } = useGetCompanyPlatformUsers(
        selectedPlatform?.id ? currentCompany : null,
        selectedPlatform?.id,
        // selectedUserType,
        currentScope,
        setSummaryData,
        (data) => {
            if (selectedPlatform?.id) {
                setPlatformsTableData((prev) => ({
                    ...prev,
                    data: data.map((r) => {
                        const displayName =
                            r.displayName ||
                            r.primaryUser?.displayName ||
                            [r.givenName, r.surname]
                                .filter(Boolean)
                                .join(' ') ||
                            r.userPrincipalName ||
                            '';
                        return {
                            ...r,
                            displayName,
                            telephoneNumber: getTelephoneNumber(r),
                        };
                    }),
                }));
            }
        },
    );

    // Keep only these declarations
    const shouldFetchTCAPUsers = filteredPlatforms?.length > 1;
    const isFetchingData =
        (shouldFetchTCAPUsers && (isLoading || isRefetching || isFetching)) ||
        isPlatformUsersRefetching;
    const refetchTableData =
        selectedPlatform?.id || selectedPlatform !== undefined
            ? refetchPlatformUsers
            : refetch;

    useEffect(() => {
        setHasNoUsers(false);
    }, [pathname]);

    // Matches the Users CSV template naming pattern, e.g. Acme_Users_260812_143022
    const titleName = `${
        slugify(currentCompanyData?.name) || 'Company'
    }_Users_${makeDateStr()}`;

    const summaryDetails = [
        {
            icon: BsPerson,
            count:
                Array.isArray(summaryData?.tcapUsers) &&
                summaryData.tcapUsers.length > 0
                    ? summaryData.tcapUsers.length
                    : Array.isArray(summaryData?.platformUsers)
                      ? summaryData.platformUsers.length
                      : 0,
            toolTip: 'Users',
        },
        {
            icon: BsPersonCheck,
            count: summaryData?.licensedPlatformUsers,
            toolTip: 'Licensed',
        },
        {
            icon: LiaHashtagSolid,
            count: summaryData?.assignedPlatformUsers,
            toolTip: 'Platform Users with Numbers Assigned',
        },
    ];

    // UsersButtons' toolbar row and PlatformFilterStack share the same
    // toolbar row. Each independently deciding "do I have enough room" off
    // its own (non-growing) box can't detect the row getting wider again —
    // growing to measure correctly would just recreate a visual gap between
    // the two. Instead, measure the row's real available width once here
    // and hand both a plain forceOverflowing boolean. The button count comes
    // from UsersButtons' own visibility rule so the two cannot disagree.
    const usersButtonCount = Object.values(
        getUsersButtonVisibility({
            selectedPlatform,
            isReAccountsPath: pathname.includes('/users'),
        }),
    ).filter(Boolean).length;
    const hasPlatformFilter = filteredPlatforms?.length > 1;

    const toolbarRowRef = useRef(null);
    const [buttonsOverflowing, setButtonsOverflowing] = useState(false);
    const [platformsOverflowing, setPlatformsOverflowing] = useState(false);

    useLayoutEffect(() => {
        const row = toolbarRowRef.current;
        if (!row || (!usersButtonCount && !hasPlatformFilter)) {
            return undefined;
        }

        const GAP = 8;

        const checkWidth = () => {
            const availableWidth = row.clientWidth;
            if (availableWidth <= 0) return;

            const buttonsFullWidth = usersButtonCount
                ? getButtonGroupFullWidth(usersButtonCount)
                : 0;
            const platformsFullWidth = hasPlatformFilter
                ? getPlatformStackFullWidth(filteredPlatforms.length)
                : 0;
            const platformsCollapsedWidth = hasPlatformFilter
                ? PLATFORM_STACK_MIN_WIDTH
                : 0;
            const betweenGap = usersButtonCount && hasPlatformFilter ? GAP : 0;

            // Priority: the platform filters collapse first; the button
            // group only collapses once buttons-full + platforms-collapsed
            // no longer fits either.
            const bothFullWidth =
                buttonsFullWidth + betweenGap + platformsFullWidth;
            const buttonsFullPlatformsCollapsedWidth =
                buttonsFullWidth + betweenGap + platformsCollapsedWidth;

            if (availableWidth >= bothFullWidth) {
                setButtonsOverflowing(false);
                setPlatformsOverflowing(false);
            } else if (availableWidth >= buttonsFullPlatformsCollapsedWidth) {
                setButtonsOverflowing(false);
                setPlatformsOverflowing(true);
            } else {
                setButtonsOverflowing(true);
                setPlatformsOverflowing(true);
            }
        };

        checkWidth();

        const resizeObserver = new ResizeObserver(checkWidth);
        resizeObserver.observe(row);
        return () => resizeObserver.disconnect();
    }, [usersButtonCount, hasPlatformFilter, filteredPlatforms?.length]);

    if (!currentCompany) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    return (
        <>
            {hasNoUsers && (
                <Alert status="info" sx={{ mb: 4 }}>
                    <AlertIcon />
                    No services syncing users
                </Alert>
            )}
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>Users</Text>
                <Box
                    mb={4}
                    p={3}
                    borderRadius="md"
                    bg={darkMode ? '#lalala' : '#EEE'}
                    display="flex"
                    alignItems="center">
                    <Text sx={{ fontWeight: '500' }} mr={2} minW="80px">
                        Summary
                    </Text>
                    <HStack
                        spacing={2}
                        wrap="wrap"
                        justifyContent="flex-end"
                        flex="1">
                        {summaryDetails.map((item, index) => (
                            <SummaryPill
                                key={index}
                                icon={item.icon}
                                count={item.count}
                                tooltip={item.toolTip}
                                sx={{
                                    marginRight:
                                        index < summaryDetails.length - 1
                                            ? '0px'
                                            : 0,
                                }}
                            />
                        ))}
                    </HStack>
                </Box>
            </HStack>
            <ServiceWarningBanner
                appAuth={appAuth}
                companyServices={companyServices}
                context="User Management"
            />
            <TroubleshootModal onClose={onClose} isOpen={isOpen} />
            <AGGridMain
                gridRef={gridRef}
                tableData={
                    selectedPlatform !== undefined
                        ? platformsTableDataWithColumns
                        : tableDataWithColumns
                }
                masterDetail={selectedPlatform !== undefined ? false : true}
                isFetching={isFetchingData}
                titleName={titleName}
                stateIdentifier={
                    selectedPlatform !== undefined
                        ? 'Users-PlatformView'
                        : 'Users-TCAPView'
                }
                refetch={refetchTableData}
                isSaveButtonEnabled
                extraComponents={
                    <HStack
                        spacing={2}
                        ref={toolbarRowRef}
                        overflow="hidden"
                        minWidth={0}
                        flexGrow={1}>
                        {
                            <>
                                <UsersButtons
                                    selectedPlatform={selectedPlatform}
                                    forceOverflowing={buttonsOverflowing}
                                />
                                <PlatformFilterStack
                                    platformList={filteredPlatforms}
                                    isSelected={(platform) =>
                                        selectedPlatform?.id === platform.id
                                    }
                                    onSelect={(platform) => {
                                        if (
                                            selectedPlatform?.id === platform.id
                                        ) {
                                            setSelectedPlatform(undefined);
                                            refetch();
                                        } else {
                                            setSelectedPlatform(platform);
                                        }
                                    }}
                                    forceOverflowing={platformsOverflowing}
                                />
                                {/* <HStack spacing={2}>
                                {/* // commented out temporarily */}
                                {/* {selectedPlatform && (
                                    <>{userTypeOptions.map((option) => (
                                        <Tooltip key={option.id} label={option.name}>
                                            <IconButton
                                                icon={option.id === 'user' ? 'User' : 'Resource Account'}
                                                aria-label={option.name}
                                                size="sm"
                                                variant="ghost"
                                                opacity={selectedUserType?.id === option.id ? 1 : 0.5}
                                                onClick={() => {
                                                    setSelectedUserType(selectedUserType?.id === option.id ? undefined : option);
                                                    if (!option) {
                                                        refetchPlatformUsers();
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    ))}</>
                                    )} */}
                                {/* <HStack spacing={2}> */}
                            </>
                        }
                    </HStack>
                }
            />
            <UserEditDrawer
                isOpen={isEditUserDrawerOpen}
                onClose={setUserDrawerClose}
                refreshTable={refetchTableData}
                selectedPlatform={editedPlatform}
                platformRefetch={platformRefetch}
                setExpandedPlatformUsers={setExpandedPlatformUsers}
                expandedPlatformUsers={expandedPlatformUsers}
                isFetchingData={isFetchingData}
                tcapUserId={userId}
                usersList={tableData?.data}
                setExpandedPlatformUsersToEmpty={
                    setExpandedPlatformUsersToEmpty
                }
                initialPrimaryUser={primaryUser}
                setPrimaryUser={setPrimaryUser}
            />
        </>
    );
}
